import React, { useState, useEffect } from 'react';
import { Plus, Pencil, Trash2, Eye, EyeOff, Save, X, BookOpen, Image, Clock, Sparkles, Power } from 'lucide-react';
import axios from 'axios';
import { blogAPI, photoAPI } from '../../lib/api';
import { Button } from '../ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const CATEGORIES = [
  { value: 'guide', label: 'Guide' },
  { value: 'comparatif', label: 'Comparatif' },
  { value: 'test', label: 'Test produit' },
  { value: 'actu', label: 'Actualité' },
];

const emptyArticle = {
  slug: '',
  title: '',
  category: 'guide',
  meta_description: '',
  keywords: [],
  excerpt: '',
  content: '',
  image: '',
  published: false,
  read_time: 5,
};

export default function AdminBlogTab() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingArticle, setEditingArticle] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [keywordInput, setKeywordInput] = useState('');
  const [autoSettings, setAutoSettings] = useState(null);
  const [generating, setGenerating] = useState(false);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const res = await blogAPI.adminGetArticles();
      setArticles(res.data);
    } catch (err) {
      toast.error('Erreur lors du chargement des articles');
    }
    setLoading(false);
  };

  const fetchAutoSettings = async () => {
    try {
      const res = await axios.get(`${API}/blog/admin/auto-settings`, { headers: authHeaders() });
      setAutoSettings(res.data);
    } catch (err) { /* silently fail */ }
  };

  useEffect(() => {
    fetchArticles();
    fetchAutoSettings();
  }, []);

  const handleAutoGenerate = async () => {
    setGenerating(true);
    try {
      const res = await axios.post(`${API}/blog/admin/auto-generate`, {}, { headers: authHeaders() });
      toast.success(`Brouillon créé : "${res.data.title}"`);
      await fetchArticles();
      await fetchAutoSettings();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Échec de la génération');
    } finally {
      setGenerating(false);
    }
  };

  const handleToggleAuto = async () => {
    if (!autoSettings) return;
    try {
      const newEnabled = !autoSettings.enabled;
      await axios.put(`${API}/blog/admin/auto-settings?enabled=${newEnabled}`, {}, { headers: authHeaders() });
      toast.success(newEnabled ? 'Auto-génération activée' : 'Auto-génération désactivée');
      fetchAutoSettings();
    } catch (err) {
      toast.error('Erreur');
    }
  };

  const handleCreate = () => {
    setEditingArticle({ ...emptyArticle });
    setIsCreating(true);
    setKeywordInput('');
  };

  const handleEdit = (article) => {
    setEditingArticle({ ...article });
    setIsCreating(false);
    setKeywordInput(article.keywords?.join(', ') || '');
  };

  const handleCancel = () => {
    setEditingArticle(null);
    setIsCreating(false);
  };

  const handleSave = async () => {
    if (!editingArticle.slug || !editingArticle.title || !editingArticle.content) {
      toast.error('Veuillez remplir le slug, le titre et le contenu');
      return;
    }

    setSaving(true);
    try {
      const articleData = {
        ...editingArticle,
        keywords: keywordInput.split(',').map(k => k.trim()).filter(Boolean),
      };

      if (isCreating) {
        await blogAPI.adminCreateArticle(articleData);
        toast.success('Article créé avec succès');
      } else {
        await blogAPI.adminUpdateArticle(editingArticle.id, articleData);
        toast.success('Article mis à jour');
      }
      
      setEditingArticle(null);
      setIsCreating(false);
      fetchArticles();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de la sauvegarde');
    }
    setSaving(false);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer cet article ?')) return;
    
    try {
      await blogAPI.adminDeleteArticle(id);
      toast.success('Article supprimé');
      fetchArticles();
    } catch (err) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleTogglePublish = async (id) => {
    try {
      const res = await blogAPI.adminTogglePublish(id);
      toast.success(res.data.message);
      fetchArticles();
    } catch (err) {
      toast.error('Erreur lors du changement de statut');
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    try {
      const res = await photoAPI.upload(file);
      setEditingArticle(prev => ({ ...prev, image: res.data.file_id }));
      toast.success('Image uploadée');
    } catch (err) {
      toast.error('Erreur lors de l\'upload de l\'image');
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  if (editingArticle) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-bold text-[#033871]">
            {isCreating ? 'Nouvel article' : 'Modifier l\'article'}
          </h2>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleCancel} disabled={saving}>
              <X className="w-4 h-4 mr-1" /> Annuler
            </Button>
            <Button onClick={handleSave} disabled={saving} className="bg-[#32CD32] hover:bg-[#28a428]">
              <Save className="w-4 h-4 mr-1" /> {saving ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Left column - Main fields */}
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-[#033871] mb-1">Titre *</label>
              <input
                type="text"
                value={editingArticle.title}
                onChange={(e) => {
                  const title = e.target.value;
                  setEditingArticle(prev => ({
                    ...prev,
                    title,
                    slug: isCreating ? generateSlug(title) : prev.slug
                  }));
                }}
                className="w-full px-3 py-2 rounded-lg border border-[#E8F4FD] focus:border-[#0C63BF] focus:ring-1 focus:ring-[#0C63BF] outline-none"
                placeholder="Titre de l'article"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#033871] mb-1">Slug (URL) *</label>
              <input
                type="text"
                value={editingArticle.slug}
                onChange={(e) => setEditingArticle(prev => ({ ...prev, slug: e.target.value }))}
                className="w-full px-3 py-2 rounded-lg border border-[#E8F4FD] focus:border-[#0C63BF] focus:ring-1 focus:ring-[#0C63BF] outline-none font-mono text-sm"
                placeholder="mon-article-seo"
              />
              <p className="text-xs text-[#5A8BA8] mt-1">URL: /blog/{editingArticle.slug || 'mon-article'}</p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-[#033871] mb-1">Catégorie</label>
                <select
                  value={editingArticle.category}
                  onChange={(e) => setEditingArticle(prev => ({ ...prev, category: e.target.value }))}
                  className="w-full px-3 py-2 rounded-lg border border-[#E8F4FD] focus:border-[#0C63BF] outline-none"
                >
                  {CATEGORIES.map(cat => (
                    <option key={cat.value} value={cat.value}>{cat.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-[#033871] mb-1">Temps de lecture (min)</label>
                <input
                  type="number"
                  min="1"
                  max="60"
                  value={editingArticle.read_time}
                  onChange={(e) => setEditingArticle(prev => ({ ...prev, read_time: parseInt(e.target.value) || 5 }))}
                  className="w-full px-3 py-2 rounded-lg border border-[#E8F4FD] focus:border-[#0C63BF] outline-none"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#033871] mb-1">Extrait (court résumé)</label>
              <textarea
                value={editingArticle.excerpt}
                onChange={(e) => setEditingArticle(prev => ({ ...prev, excerpt: e.target.value }))}
                rows={2}
                className="w-full px-3 py-2 rounded-lg border border-[#E8F4FD] focus:border-[#0C63BF] focus:ring-1 focus:ring-[#0C63BF] outline-none resize-none"
                placeholder="Résumé court pour les aperçus..."
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#033871] mb-1">Meta description (SEO)</label>
              <textarea
                value={editingArticle.meta_description}
                onChange={(e) => setEditingArticle(prev => ({ ...prev, meta_description: e.target.value }))}
                rows={2}
                maxLength={160}
                className="w-full px-3 py-2 rounded-lg border border-[#E8F4FD] focus:border-[#0C63BF] focus:ring-1 focus:ring-[#0C63BF] outline-none resize-none"
                placeholder="Description pour les moteurs de recherche (160 car. max)"
              />
              <p className="text-xs text-[#5A8BA8] mt-1">{editingArticle.meta_description?.length || 0}/160 caractères</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#033871] mb-1">Mots-clés (SEO)</label>
              <input
                type="text"
                value={keywordInput}
                onChange={(e) => setKeywordInput(e.target.value)}
                className="w-full px-3 py-2 rounded-lg border border-[#E8F4FD] focus:border-[#0C63BF] outline-none"
                placeholder="meilleure raquette padel, comparatif, guide..."
              />
              <p className="text-xs text-[#5A8BA8] mt-1">Séparez les mots-clés par des virgules</p>
            </div>

            <div>
              <label className="block text-sm font-medium text-[#033871] mb-1">Image de couverture</label>
              <div className="flex items-center gap-3">
                {editingArticle.image && (
                  <img 
                    src={photoAPI.getUrl(editingArticle.image)} 
                    alt="Cover" 
                    className="w-20 h-20 object-cover rounded-lg"
                  />
                )}
                <label className="flex items-center gap-2 px-4 py-2 bg-[#F0F7FA] rounded-lg cursor-pointer hover:bg-[#E8F4FD] transition-colors">
                  <Image className="w-4 h-4 text-[#0C63BF]" />
                  <span className="text-sm text-[#033871]">Choisir une image</span>
                  <input type="file" accept="image/*" onChange={handleImageUpload} className="hidden" />
                </label>
              </div>
            </div>

            <div className="flex items-center gap-3 p-3 bg-[#F0F7FA] rounded-lg">
              <input
                type="checkbox"
                id="published"
                checked={editingArticle.published}
                onChange={(e) => setEditingArticle(prev => ({ ...prev, published: e.target.checked }))}
                className="w-4 h-4 rounded border-[#D6E8F0] text-[#0C63BF] focus:ring-[#0C63BF]"
              />
              <label htmlFor="published" className="text-sm font-medium text-[#033871]">
                Publier l&apos;article
              </label>
            </div>
          </div>

          {/* Right column - Content */}
          <div>
            <label className="block text-sm font-medium text-[#033871] mb-1">Contenu (Markdown) *</label>
            <textarea
              value={editingArticle.content}
              onChange={(e) => setEditingArticle(prev => ({ ...prev, content: e.target.value }))}
              rows={25}
              className="w-full px-3 py-2 rounded-lg border border-[#E8F4FD] focus:border-[#0C63BF] focus:ring-1 focus:ring-[#0C63BF] outline-none resize-none font-mono text-sm"
              placeholder="## Mon article&#10;&#10;Écrivez votre contenu en Markdown...&#10;&#10;### Sous-titre&#10;- Point 1&#10;- Point 2&#10;&#10;[Lien vers le catalogue](/catalogue)"
            />
            <p className="text-xs text-[#5A8BA8] mt-1">
              Utilisez Markdown: ## pour titres, **gras**, *italique*, [lien](url), - pour listes
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Auto-generation panel */}
      {autoSettings && (
        <div className="bg-gradient-to-r from-[#033871] to-[#0C63BF] rounded-xl p-4 sm:p-5 text-white" data-testid="auto-gen-panel">
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <Sparkles className="w-4 h-4 text-[#32CD32]" />
                <h3 className="font-bold text-sm sm:text-base">Génération automatique d&apos;articles SEO</h3>
                <span className={`text-[10px] px-2 py-0.5 rounded-full font-bold ${autoSettings.enabled ? 'bg-[#32CD32] text-white' : 'bg-white/30 text-white'}`}>
                  {autoSettings.enabled ? 'ACTIVÉ' : 'PAUSE'}
                </span>
              </div>
              <p className="text-xs text-white/75 mb-1">
                {autoSettings.schedule}
              </p>
              <p className="text-[11px] text-white/60">
                {autoSettings.auto_total} articles générés · {autoSettings.draft_count} brouillons en attente de validation
                {autoSettings.last_run && (
                  <> · Dernier : {new Date(autoSettings.last_run).toLocaleString('fr-FR')}</>
                )}
              </p>
            </div>
            <div className="flex items-center gap-2 shrink-0">
              <Button
                size="sm"
                onClick={handleAutoGenerate}
                disabled={generating}
                className="bg-[#32CD32] hover:bg-[#28a428] text-white h-9 px-3 gap-1.5"
                data-testid="auto-gen-now-btn"
              >
                <Sparkles className="w-3.5 h-3.5" />
                {generating ? 'Génération…' : 'Générer maintenant'}
              </Button>
              <Button
                size="sm"
                variant="outline"
                onClick={handleToggleAuto}
                className="bg-white/10 hover:bg-white/20 border-white/30 text-white h-9 px-3 gap-1.5"
                data-testid="auto-gen-toggle-btn"
              >
                <Power className="w-3.5 h-3.5" />
                {autoSettings.enabled ? 'Mettre en pause' : 'Activer'}
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-bold text-[#033871]">Articles du blog</h2>
          <p className="text-sm text-[#5A8BA8]">{articles.length} article{articles.length > 1 ? 's' : ''}</p>
        </div>
        <Button onClick={handleCreate} className="bg-[#0C63BF] hover:bg-[#033871] gap-2">
          <Plus className="w-4 h-4" /> Nouvel article
        </Button>
      </div>

      {loading ? (
        <div className="space-y-3">
          {[1, 2, 3].map(i => (
            <div key={i} className="bg-white rounded-xl p-4 animate-pulse">
              <div className="h-5 bg-[#E8F4FD] rounded w-1/3 mb-2" />
              <div className="h-4 bg-[#E8F4FD] rounded w-2/3" />
            </div>
          ))}
        </div>
      ) : articles.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-xl">
          <BookOpen className="w-12 h-12 text-[#B8D4E3] mx-auto mb-3" />
          <p className="text-[#5A8BA8]">Aucun article pour le moment</p>
          <Button onClick={handleCreate} className="mt-4 bg-[#0C63BF]">
            Créer le premier article
          </Button>
        </div>
      ) : (
        <div className="space-y-3">
          {articles.map(article => (
            <div key={article.id} className="bg-white rounded-xl p-4 border border-[#E8F4FD] hover:shadow-md transition-shadow">
              <div className="flex items-start justify-between gap-4">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className={`px-2 py-0.5 rounded-full text-[10px] font-semibold ${
                      article.category === 'guide' ? 'bg-blue-100 text-blue-700' :
                      article.category === 'comparatif' ? 'bg-purple-100 text-purple-700' :
                      article.category === 'test' ? 'bg-amber-100 text-amber-700' :
                      'bg-green-100 text-green-700'
                    }`}>
                      {CATEGORIES.find(c => c.value === article.category)?.label || article.category}
                    </span>
                    {article.published ? (
                      <span className="flex items-center gap-1 text-xs text-green-600">
                        <Eye className="w-3 h-3" /> Publié
                      </span>
                    ) : (
                      <span className="flex items-center gap-1 text-xs text-amber-600">
                        <EyeOff className="w-3 h-3" /> Brouillon
                      </span>
                    )}
                    <span className="flex items-center gap-1 text-xs text-[#B8D4E3]">
                      <Clock className="w-3 h-3" /> {article.read_time} min
                    </span>
                    {article.views > 0 && (
                      <span className="text-xs text-[#B8D4E3]">{article.views} vues</span>
                    )}
                  </div>
                  <h3 className="font-bold text-[#033871] truncate">{article.title}</h3>
                  <p className="text-sm text-[#5A8BA8] truncate">/blog/{article.slug}</p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleTogglePublish(article.id)}
                    className="text-[#5A8BA8] hover:text-[#033871]"
                  >
                    {article.published ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(article)}
                    className="text-[#0C63BF]"
                  >
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDelete(article.id)}
                    className="text-red-500 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
